import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import FlagIcon from './FlagIcon';
import { useLanguage } from '../../contexts';

// #106: 외국인 사용자가 본인 언어를 빠르게 찾도록 원어 병기 (글로벌 표준)
// 국기 아이콘은 Figma `icon/lang/*` 컴포넌트 SVG 사용 (대한민국 국기 건곤감리 포함)
const languages = [
  { code: 'KOR', label: '한국어' },
  { code: 'ENG', label: 'English' },
  { code: 'CHN', label: '中文' },
  { code: 'JPN', label: '日本語' },
  { code: 'SPA', label: 'Español' },
];

export default function LanguageSelector() {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const { lang, setLang } = useLanguage();
  const selected = languages.find((l) => l.code === lang) || languages[0];
  const ref = useRef(null);
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);
  const DROPDOWN_W = 160;

  useEffect(() => {
    function handleClick(e) {
      if (
        ref.current && !ref.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // 드롭다운 열릴 때 버튼 위치 기반으로 좌표 계산 (portal 용)
  useEffect(() => {
    if (!open || !btnRef.current) return;
    const updatePos = () => {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.right - DROPDOWN_W });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        ref={btnRef}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 w-[80px] h-8 px-2 border border-mono-200 rounded-xl text-2xs font-semibold text-mono-900 hover:border-text-muted transition"
      >
        <FlagIcon lang={selected.code} size={18} />
        <span>{selected.code}</span>
        <Icon name="chevron-down" size={12} className="text-mono-600 ml-auto" />
      </button>
      {open && createPortal(
        <div
          ref={dropdownRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: DROPDOWN_W }}
          className="bg-white border border-mono-200 rounded-xl shadow-lg py-1 z-[100]"
        >
          {languages.map((l) => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-2sm hover:bg-mono-50 transition ${
                selected.code === l.code ? 'text-orange-500 font-bold' : 'text-mono-900'
              }`}
            >
              <FlagIcon lang={l.code} size={18} />
              <span>{l.label}</span>
              <span className="text-mono-400 ml-auto text-2xs">{l.code}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
